import { useAppSelector } from 'hooks/reduxHooks'
import Moment from 'moment/moment'
import { getClosestAirport } from 'selectors/flightsSelectors'
import { isFlexibleMonthsWithinDealDates } from 'components/Search/utils'

interface TravelDatesOptions {
  checkInDate?: Moment.Moment;
  flexibleMonths?: string;
}

function useSearchFlightDeal(
  flightDeals: Array<App.FlightDeal>,
  travelDate: TravelDatesOptions,
  destinationAirportCode?: 'DPS' | 'MLE' | 'NAN' | 'VLI',
): App.FlightDeal | undefined {
  const closestAirport = useAppSelector(getClosestAirport)

  if (!closestAirport) {
    return undefined
  }

  const filterDateRange = (deal: App.FlightDeal): boolean => {
    const dealStart = Moment(deal.salesStartDate)
    const dealEnd = Moment(deal.salesEndDate)
    const { checkInDate, flexibleMonths } = travelDate

    if (deal.hierarchy && deal.hierarchy >= 2) {
      return false
    }

    if (checkInDate) {
      return checkInDate.isBetween(dealStart, dealEnd, null, '[]')
    }

    if (flexibleMonths) {
      return isFlexibleMonthsWithinDealDates(flexibleMonths, dealStart, dealEnd)
    }

    // If neither checkInDate nor flexibleMonths is provided, include all deals
    return true
  }

  const availableDeals = flightDeals.filter(filterDateRange)

  // Find the index of the flight deal based on the closest airport and optional destination airport code
  const index = availableDeals
    .sort((a, b) => {
      const hierarchyOrder = [0, 1, undefined]
      return hierarchyOrder.indexOf(a.hierarchy) - hierarchyOrder.indexOf(b.hierarchy)
    })
    .findIndex(deal =>
      deal.originAirportCode === closestAirport.code &&
      (!destinationAirportCode || deal.destinationAirportCode === destinationAirportCode),
    )

  return index !== -1 ? availableDeals[index] : undefined
}

export default useSearchFlightDeal
